import React from "react";
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { formatIsoDuration } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";
import RunStatusChip from "./RunStatusChip";
import RunTimestamp from "./RunTimestamp";
import type {
  IntegrationRunListItem,
  IntegrationRunSortField,
} from "features/integrationsBeta/integrationsBeta";
import {
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";

const noWrapCellStyle = {
  whiteSpace: "nowrap" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
};

const COLUMN_COUNT = 5;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: COLUMN_COUNT }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width="80%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

type Props = {
  runs: IntegrationRunListItem[];
  isLoading: boolean;
  isFetching: boolean;
  selectedRunId?: number | null;
  onSelectRun: (run: IntegrationRunListItem) => void;
  sortBy?: IntegrationRunSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: IntegrationRunSortField) => void;
  statusLabel: (value: string | null | undefined) => string;
  triggerLabel: (value: string | null | undefined) => string;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
  pagination?: React.ReactNode;
};

export default function RunsHistoryTable({
  runs,
  isLoading,
  isFetching,
  selectedRunId,
  onSelectRun,
  sortBy,
  sortOrder,
  onSortChange,
  statusLabel,
  triggerLabel,
  timezoneMode,
  displayZone,
  pagination,
}: Props) {
  const sortHeader = (field: IntegrationRunSortField, label: string) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : "asc"}
        onClick={() => onSortChange(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const timeSuffix = timeHeaderSuffix(timezoneMode);
  const timeLabel = (label: string) =>
    timeSuffix ? `${label} · ${timeSuffix}` : label;
  const showCellAbbreviation = timezoneMode === "client";

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 260,
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 700, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              {sortHeader("started_at", timeLabel("Started"))}
              {sortHeader("finished_at", timeLabel("Finished"))}
              {sortHeader("duration", "Duration")}
              {sortHeader("trigger_type", "Trigger")}
              {sortHeader("status", "Status")}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  sx={{ py: 4, borderBottom: "none" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No runs yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => {
                const isSelected = selectedRunId === run.id;
                return (
                  <TableRow
                    key={run.id}
                    selected={isSelected}
                    onClick={() => onSelectRun(run)}
                    sx={{
                      cursor: "pointer",
                      opacity: isFetching ? 0.6 : 1,
                      "&:last-child td": { borderBottom: "none" },
                      "&:hover": {
                        bgcolor: "calibrate.primarySoft06",
                      },
                    }}
                  >
                    <TableCell sx={noWrapCellStyle}>
                      <RunTimestamp
                        iso={run.started_at}
                        timeZone={displayZone}
                        showAbbreviation={showCellAbbreviation}
                      />
                    </TableCell>
                    <TableCell sx={noWrapCellStyle}>
                      {run.finished_at ? (
                        <RunTimestamp
                          iso={run.finished_at}
                          timeZone={displayZone}
                          showAbbreviation={showCellAbbreviation}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={noWrapCellStyle}>
                      {formatIsoDuration(run.started_at, run.finished_at)}
                    </TableCell>
                    <TableCell sx={noWrapCellStyle}>
                      <Typography variant="body2">
                        {triggerLabel(run.trigger_type)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <RunStatusChip
                        status={run.status}
                        label={statusLabel(run.status)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
